"use client"

import { copyToClipboard } from "@/utils/copy"
import { classes } from "@/utils"
import { ComponentChildren } from "preact"

const COPY_SUCCESS = "Copied promotion ID"

export function parse_remote_promo_ids(raw: string): string[] {
  return raw
    .split(/\s*,\s*/)
    .map(s => s.trim())
    .filter(Boolean)
}

interface ClickToCopyRemotePromoIdProps {
  copyValue: string
  className?: string
  /** Prevents parent row/card click handlers from firing (e.g. table rows, calendar blocks). */
  stopPropagation?: boolean
  children?: ComponentChildren
  title?: string
}

export function ClickToCopyRemotePromoId({
  copyValue,
  className,
  stopPropagation = false,
  children,
  title = "Click to copy promotion ID",
}: ClickToCopyRemotePromoIdProps) {
  const trimmed = copyValue.trim()
  if (!trimmed) {
    return <>{children}</>
  }

  function bubbleGuard(e: MouseEvent) {
    if (!stopPropagation) return
    e.preventDefault()
    e.stopPropagation()
  }

  async function handleClick(e: MouseEvent) {
    bubbleGuard(e)
    await copyToClipboard(trimmed, COPY_SUCCESS)
  }

  return (
    <button
      type="button"
      title={title}
      class={classes(
        "cursor-pointer rounded border-0 bg-transparent p-0 font-inherit text-inherit hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1",
        className
      )}
      onMouseDown={stopPropagation ? bubbleGuard : undefined}
      onClick={handleClick}
    >
      {children ?? trimmed}
    </button>
  )
}

interface ClickToCopyRemotePromoIdsDisplayProps {
  value: string
  className?: string
}

/** Renders comma-separated remote promo ids; each segment copies individually on click. */
export function ClickToCopyRemotePromoIdsDisplay({ value, className }: ClickToCopyRemotePromoIdsDisplayProps) {
  const ids = parse_remote_promo_ids(value)
  if (ids.length === 0) {
    return <span class={className}>{value}</span>
  }
  return (
    <span class={className}>
      {ids.map((id, i) => (
        <span key={`${id}-${i}`}>
          {i > 0 ? ", " : null}
          <ClickToCopyRemotePromoId copyValue={id} className="inline hover:underline">
            {id}
          </ClickToCopyRemotePromoId>
        </span>
      ))}
    </span>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("parse_remote_promo_ids", () => {
    it("splits comma-separated ids and trims", () => {
      expect(parse_remote_promo_ids("GP-001, GP-002")).toEqual(["GP-001", "GP-002"])
    })

    it("returns a single entry when there is no comma", () => {
      expect(parse_remote_promo_ids("  RP-9  ")).toEqual(["RP-9"])
    })

    it("filters empty segments", () => {
      expect(parse_remote_promo_ids("A,, B")).toEqual(["A", "B"])
    })
  })
}
